import { z } from "zod";

export const flooringTypes = [
  "Hardwood",
  "Vinyl",
  "Carpet",
  "Tile",
  "Laminate",
  "Not Sure",
] as const;

export const roomTypes = [
  "Living Room",
  "Bedroom",
  "Kitchen",
  "Bathroom",
  "Dining Room",
  "Hallway",
  "Basement",
  "Other",
] as const;

export const currentFlooringOptions = [
  "Hardwood",
  "Carpet",
  "Tile",
  "Vinyl",
  "Laminate",
  "Concrete",
  "Other",
  "Not Sure",
] as const;

export const timeSlots = [
  "Morning (9am-12pm)",
  "Afternoon (12pm-4pm)",
  "Evening (4pm-7pm)",
] as const;

// Step 1: Flooring Type
export const flooringTypeSchema = z.object({
  flooringTypes: z
    .array(z.enum(flooringTypes))
    .min(1, "Please select at least one flooring type"),
});

// Step 2: Space Details
export const spaceDetailsSchema = z.object({
  approximateSize: z
    .string()
    .min(1, "Please enter the approximate size")
    .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
      message: "Please enter a valid number",
    }),
  rooms: z.array(z.enum(roomTypes)).min(1, "Please select at least one room"),
  currentFlooring: z.enum(currentFlooringOptions, {
    required_error: "Please select your current flooring type",
  }),
});

// Step 3: Contact Info
export const contactInfoSchema = z.object({
  fullName: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  email: z
    .string()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  phone: z
    .string()
    .min(10, "Please enter a valid phone number")
    .max(20, "Phone number is too long")
    .regex(/^[\d\s\-\(\)\+]+$/, "Please enter a valid phone number"),
  address: z
    .string()
    .min(5, "Please enter your address")
    .max(200, "Address is too long"),
  city: z
    .string()
    .min(2, "Please enter your city")
    .max(100, "City name is too long"),
  zipCode: z
    .string()
    .min(5, "Please enter a valid ZIP code")
    .max(10, "ZIP code is too long")
    .regex(/^\d{5}(-\d{4})?$/, "Please enter a valid ZIP code"),
});

// Step 4: Scheduling
export const schedulingSchema = z.object({
  preferredDate: z.date({
    required_error: "Please select a preferred date",
  }),
  preferredTime: z.enum(timeSlots, {
    required_error: "Please select a preferred time",
  }),
  additionalNotes: z.string().max(1000, "Notes are too long").optional(),
});

// Combined schema for the entire form
export const estimateFormSchema = z.object({
  ...flooringTypeSchema.shape,
  ...spaceDetailsSchema.shape,
  ...contactInfoSchema.shape,
  ...schedulingSchema.shape,
});

export type FlooringTypeData = z.infer<typeof flooringTypeSchema>;
export type SpaceDetailsData = z.infer<typeof spaceDetailsSchema>;
export type ContactInfoData = z.infer<typeof contactInfoSchema>;
export type SchedulingData = z.infer<typeof schedulingSchema>;
export type EstimateFormData = z.infer<typeof estimateFormSchema>;
